import React, { useEffect, useRef, useState } from "react";
import { ROLE_EDIT_ICON, ROLE_VIEW_ICON } from "../../utils/aap-image-constant";
import TableHeading from "../../components/common/TableHeading";
import { useLocation, useNavigate } from "react-router-dom";
import { ROUTES } from "../../hooks/routes/routes-constant";
import { useTranslation } from "react-i18next";
import { getAllRoles, RoleStatus } from "../../hooks/services/api-services";
import { useSelector } from "react-redux";
import { decryptAEStoJSON, toastEmitter } from "../../utils/utilities";
import { API_RESPONSE, DEFAULT_PAGE_LENGTH } from "../../utils/app-constants";
import BreadCrum from "../../components/common/BreadCrum";
import TableLayout from "../../components/layouts/table-layout";
import Staff from "./staff";
import AccessPermissionpage from "../../components/common/accessPermissionpage";

const Role = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const isFirstRender = useRef(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [pending, setPending] = useState(false);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_LENGTH);
  const [sortColumn, setSortColumn] = useState("");
  const [sortDirection, setSortDirection] = useState("");
  const [totalRows, setTotalRows] = useState(0);
  const { value } = useSelector((state) => state?.loggedUser);
  const userData = decryptAEStoJSON(value);
  const [activeTab, setActiveTab] = useState(
    location?.state?.activeTab || "role"
  );
  const { i18n } = useTranslation();

  const payload = {
    pageIndex: searchTerm === "" ? page || 0 : 0,
    pageSize: pageSize || DEFAULT_PAGE_LENGTH,
    searchText: searchTerm,
    direction: sortDirection || "desc",
    filterByFieldName: sortColumn || "id",
    status: 0,
    clinicId: userData?.id || 0,
    doctorId: 0,
    languageType: i18n.language
  };

  const [rowData, setRowData] = useState([]);

  const fetchRoleData = async () => {
    setPending(true);
    try {
      const response = await getAllRoles(payload);

      if (response?.data?.status === 200) {
        const roleList = response?.data?.data?.roleList || [];
        const filteredList = roleList.filter((item) => item && item.id); // removes null or invalid items

        setRowData(filteredList);
        setTotalRows(response?.data?.data?.totalElement || 0);
      } else {

        setRowData([]);
        setTotalRows(0);

      }
    } catch (err) {
      if (isFirstRender.current) {
        toastEmitter("error", API_RESPONSE?.MESSAGE_503);
      }
      setRowData([]);
    } finally {
      setPending(false);
      isFirstRender.current = false; // avoid showing toast multiple times
    }
  };


  useEffect(() => {
    if (activeTab === "role" && userData?.id) {
      fetchRoleData();
    }
  }, [page, pageSize, searchTerm, sortColumn, sortDirection, activeTab, i18n.language, userData?.id]);

  const handleRoleStatus = async (id, status) => {
    try {
      const response = await RoleStatus(id, status);
      if (response.status !== 200) {
        toastEmitter("error", response?.data?.message);
      } else {
        toastEmitter("success", response?.data?.message);
        fetchRoleData();
      }
    } catch (err) {
      toastEmitter("error", API_RESPONSE?.MESSAGE_503);
    }
  };
  // Table Columns

  const tableColumnsRole = [
    {
      name: t("S.No"),
      width: "100px",
      selector: (row, index) => `${page * pageSize + index + 1}`,
    },

    {
      code: "role_Name",
      name: t("Role Name"),
      width: "250px",
      sortable: true,
      selector: (row) => (
        <div className="fw-600">

          {row?.roleName}
        </div>
      )
    },

    {
      name: t("Description"),
      sortable: false,
      width: "250px",
      selector: (row) => (
        <div className="cursor-pointer " title={row.roleDescription}>
          {row?.roleDescription}
        </div>)
    },
    {
      code: "status",
      sortable: true,

      name: t("Status"),
      selector: (row) => row?.status,
      cell: (row) => (

        <div
          className={`d-flex align-items-center gap-1 px-2 py-1 rounded-4 cursor-pointer
    ${row.status === 1
              ? "bg-green-normal color-green-bold"
              : "bg-gray-normal color-gray-bold"
            }
    ${permissionAccess?.moduleAction === 0 ? "disabled-style" : ""}
  `}
          onClick={(e) => {
            if (permissionAccess?.moduleAction !== 0) {
              handleRoleStatus(row?.id, row?.status === 1 ? 0 : 1);
            }
          }}
        >
          <div
            className={`rounded-circle ${row?.status === 1 ? "bg-green-bold" : "bg-gray-bold"
              }`}
            style={{ width: "10px", height: "10px" }}
          ></div>
          <p className="m-0 fs-14 fw-normal">
            {row?.status === 1 ? t("Active") : t("Inactive")}
          </p>
        </div>
      ),
    },
    {
      name: t("Action"),
      width: "120px",
      compact: true,
      cell: (row) => {
        const canEdit =
          userData?.responsePayload?.roleName !== row?.roleName &&
          permissionAccess?.updateAction === 1;
        const canView = permissionAccess?.viewAction === 1;

        return (
          <div className="d-flex justify-content-between align-items-center">
            <div
              className={`me-2 cursor-pointer ${!canEdit ? "disabled-style" : ""
                } `}
              onClick={() => {
                if (canEdit) handleEdit(row?.id);
              }}
              style={!canEdit ? { pointerEvents: "none", opacity: 0.6 } : {}}
            >
              <img src={ROLE_EDIT_ICON} alt="Edit" />
            </div>

            <div
              className={`me-2 cursor-pointer ${!canView ? "disabled-style" : ""
                }`}
              onClick={() => {
                if (canView) handleView(row?.id);
              }}
              style={!canView ? { pointerEvents: "none", opacity: 0.6 } : {}}
            >
              <img src={ROLE_VIEW_ICON} alt="View" />
            </div>
          </div>
        );
      },
    },
  ];

  const handleEdit = (id) => {
    navigate(`${ROUTES.ROLE_MANAGEMENT}/role/edit`, {
      state: { formType: "edit", roleId: id, activeTab },
    });
  };
  const handleAdd = (id) => {
    navigate(`${ROUTES.ROLE_MANAGEMENT}/role/add`, {
      state: { formType: "add", roleId: id, activeTab },
    });
  };
  const handleView = (id) => {
    navigate(`${ROUTES.ROLE_MANAGEMENT}/role/view`, {
      state: { formType: "view", roleId: id, activeTab },
    });
  };
  // permission code for this module start
  const [permissionAccess, setPermissionAccess] = useState();
  const rawPermission = useSelector((state) => state?.permission?.value);
  const permissions = decryptAEStoJSON(rawPermission);
  useEffect(() => {
    const RoleAccessFilterData =
      permissions &&
      permissions?.filter((v) => v?.moduleName === "Role Management");
    setPermissionAccess(RoleAccessFilterData?.[0]);
  }, []);
  // permission code for this module end
  return (
    <>
      {permissionAccess?.addAction === 0 &&
        permissionAccess?.updateAction === 0 &&
        permissionAccess?.viewAction === 0 ? (
        <>
          <AccessPermissionpage />
        </>
      ) : (
        <>
          <div>
            <div className="main_datatable my-lg-3 my-2 bank_tabs">
              <BreadCrum
                firstData={t("Role Management")}
                className="fw-600 fs-20"
              />
              <ul className="nav nav-pills gap-2" id="pills-tab" role="tablist">
                <li className="nav-item" role="presentation ">
                  <button
                    className={`nav-link mb-2 ${activeTab === "role" ? "active" : ""
                      } `}
                    onClick={() => setActiveTab("role")}
                  >
                    {t("Role")}
                  </button>
                </li>
                <li className="nav-item" role="presentation">
                  <button
                    className={`nav-link ms-sm-2 ms-0 mb-2 ${i18n.language === "ar" ? "me-0 me-sm-2" : ""
                      } ${activeTab === "staff" ? "active" : ""}`}
                    onClick={() => setActiveTab("staff")}
                  >
                    {t("Staff")}
                  </button>
                </li>
              </ul>

              <div className="tab-content">
                {activeTab === "role" && (
                  <div className="tab-pane fade show active">
                    <TableHeading
                      title={t("Role")}
                      searchValue={searchTerm}
                      setSearchValue={setSearchTerm}
                      data="Role"
                      showbutton={permissionAccess?.addAction === 1}
                      addButtonClick={() => handleAdd()}
                    />

                    <div className="table-wrapper">
                      <TableLayout
                        _tblColumns={tableColumnsRole}
                        _rowData={rowData}
                        pending={pending}
                        pagination={true}
                        selectableRows={false}
                        setPending={setPending}
                        _totalRows={totalRows}
                        pageSize={pageSize}
                        setPageSize={setPageSize}
                        setPage={setPage}
                        setSortColumn={setSortColumn}
                        setSortDirection={setSortDirection}
                      />
                    </div>
                  </div>
                )}
                {activeTab === "staff" && (
                  <div className="tab-pane fade show active">
                    <Staff />
                  </div>
                )}
              </div>
            </div>
          </div>
        </>
      )}
    </>
  );
};

export default Role;
